/**
 * Payment Service
 * Frontend service for payment gateway integration
 */

import { apiClient } from './api.client';
import { currencyFormattingService } from './currency-formatting.service';

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'UPI' | 'CARD' | 'NET_BANKING' | 'BANK_TRANSFER';
  enabled: boolean;
  minAmount: number;
  maxAmount: number;
  processingFee: number;
  estimatedTime: string;
  supportedBanks?: string[];
}

export interface PaymentOrder {
  orderId: string;
  amount: number;
  currency: string;
  redirectUrl?: string;
  qrCode?: string;
  upiLink?: string;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  method: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
  createdAt: string;
  completedAt?: string;
  failureReason?: string;
  receipt?: {
    id: string;
    invoiceNumber: string;
    status: string;
  };
}

export interface PaymentHistory {
  transactions: PaymentTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface Receipt {
  id: string;
  invoiceNumber: string;
  amount: number;
  gstAmount: number;
  totalAmount: number;
  paymentMethod: string;
  status: 'GENERATED' | 'EMAILED' | 'FAILED';
  generatedAt: string;
  emailedAt?: string;
  transaction: {
    orderId: string;
    gatewayTransactionId?: string;
    createdAt: string;
    completedAt?: string;
  };
}

class PaymentService {
  private baseUrl = '/payments'; // Fixed: removed duplicate /api prefix

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/methods`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw new Error('Failed to fetch payment methods');
    }
  }

  async initiatePayment(
    amount: number,
    method: string,
    description?: string
  ): Promise<PaymentOrder> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/initiate`, {
        amount,
        method,
        description
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error initiating payment:', error);
      const message = error.response?.data?.error || 'Failed to initiate payment';
      throw new Error(message);
    }
  }

  async getPaymentStatus(orderId: string): Promise<PaymentTransaction> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/status/${orderId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching payment status:', error);
      const message = error.response?.data?.error || 'Failed to fetch payment status';
      throw new Error(message);
    }
  }

  async cancelPayment(orderId: string): Promise<boolean> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/cancel/${orderId}`);
      return response.data.success;
    } catch (error: any) {
      console.error('Error cancelling payment:', error);
      const message = error.response?.data?.error || 'Failed to cancel payment';
      throw new Error(message);
    }
  }

  async getPaymentHistory(
    page: number = 1,
    limit: number = 20,
    status?: string,
    method?: string
  ): Promise<PaymentHistory> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) params.append('status', status);
      if (method) params.append('method', method);

      const response = await apiClient.get(`${this.baseUrl}/history?${params}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching payment history:', error);
      const message = error.response?.data?.error || 'Failed to fetch payment history';
      throw new Error(message);
    }
  }

  async getReceiptHistory(limit: number = 50): Promise<Receipt[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/receipts?limit=${limit}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching receipt history:', error);
      const message = error.response?.data?.error || 'Failed to fetch receipt history';
      throw new Error(message);
    }
  }

  async downloadReceipt(receiptId: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/receipts/${receiptId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      console.error('Error downloading receipt:', error);
      const message = error.response?.data?.error || 'Failed to download receipt';
      throw new Error(message);
    }
  }

  async emailReceipt(receiptId: string): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/receipts/${receiptId}/email`);
    } catch (error: any) {
      console.error('Error emailing receipt:', error);
      const message = error.response?.data?.error || 'Failed to email receipt';
      throw new Error(message);
    }
  }

  // Utility methods
  formatAmount(amount: number): string {
    return currencyFormattingService.formatCurrencyFromMinorUnit(amount, 'INR');
  }

  formatAmountWithGST(amount: number): { base: string; gst: string; total: string } {
    const baseAmount = amount / 100;
    return currencyFormattingService.formatCurrencyWithGST(baseAmount, 0.18, 'INR');
  }

  getPaymentStatusColor(status: string): string {
    switch (status) {
      case 'SUCCESS':
        return 'text-green-600 bg-green-100';
      case 'FAILED':
        return 'text-red-600 bg-red-100';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-100';
      case 'CANCELLED':
        return 'text-gray-600 bg-gray-100';
      case 'EXPIRED':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  getPaymentMethodIcon(method: string): string {
    switch (method) {
      case 'UPI':
        return '📱';
      case 'CARD':
        return '💳';
      case 'NET_BANKING':
        return '🏦';
      case 'BANK_TRANSFER':
        return '💰';
      default:
        return '💳';
    }
  }

  // Polling utility for payment status
  async pollPaymentStatus(
    orderId: string,
    onStatusChange: (status: PaymentTransaction) => void,
    maxAttempts: number = 60,
    interval: number = 3000
  ): Promise<PaymentTransaction> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      
      const poll = async () => {
        try {
          attempts++;
          const status = await this.getPaymentStatus(orderId);
          onStatusChange(status);

          if (status.status === 'SUCCESS' || status.status === 'FAILED') {
            resolve(status);
            return;
          }

          if (attempts >= maxAttempts) {
            reject(new Error('Payment status polling timeout'));
            return;
          }

          setTimeout(poll, interval);
        } catch (error) {
          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            setTimeout(poll, interval);
          }
        }
      };

      poll();
    });
  }

  // Validate payment amount
  validateAmount(amount: number, method?: PaymentMethod): string | null {
    if (!amount || amount <= 0) {
      return 'Please enter a valid amount';
    }

    if (method) {
      const minAmount = method.minAmount / 100;
      const maxAmount = method.maxAmount / 100;

      if (amount < minAmount) {
        return `Minimum amount for ${method.name} is ${currencyFormattingService.formatCurrency(minAmount, 'INR')}`;
      }

      if (amount > maxAmount) {
        return `Maximum amount for ${method.name} is ${currencyFormattingService.formatCurrency(maxAmount, 'INR')}`;
      }
    }

    return null;
  }

  // Generate payment summary
  generatePaymentSummary(amount: number, method: PaymentMethod) {
    const baseAmount = amount;
    const processingFee = method.processingFee / 100;
    const subtotal = baseAmount + processingFee;
    const gstAmount = subtotal * 0.18;
    const totalAmount = subtotal + gstAmount;

    return {
      baseAmount: this.formatAmount(baseAmount * 100),
      processingFee: processingFee > 0 ? this.formatAmount(method.processingFee) : null,
      subtotal: this.formatAmount(subtotal * 100),
      gstAmount: this.formatAmount(gstAmount * 100),
      totalAmount: this.formatAmount(totalAmount * 100),
      estimatedTime: method.estimatedTime
    };
  }
}

export const paymentService = new PaymentService();